import * as define from "~/definitions";
import { useAbsoluteUrl } from "~/composables/useAbsoluteUrl";
import type { UseHeadInput, UseSeoMetaInput } from "@unhead/vue/types";

export default defineNuxtPlugin((nuxt) => {
  interface page {
    title: string | false; // same as the name tag
    description: string | false;
    keywords: string[] | false;
    author: string | false;
  }

  interface og {
    description: string | false;
    applicationName?: string | false;
    author: string | false;

    ogUrl: string | false; // This should be the undecorated URL
    ogTitle: string | false;
    ogDescription: string | false;
    ogType?:
      | "website"
      | "article"
      | "book"
      | "profile"
      // Namespace URI https://ogp.me/ns/music#
      | "music.song"
      | "music.album"
      | "music.playlist"
      | "music.radio_status"
      // Namespace URI https://ogp.me/ns/video#
      | "video.movie"
      | "video.episode"
      | "video.tv_show"
      | "video.other";

    ogImageUrl: string | false;
    ogImageAlt: string | false;

    twitterCard?: "summary" | "summary_large_image" | "app" | "player" | false;
    twitterTitle: string | false;
    twitterDescription: string | false;
    twitterImageAlt: string | false;
    twitterImage: string | false;
  }

  const advanced = (page: page, og: og) => {
    var head: UseHeadInput = {
      meta: [{ property: "og:logo", content: useAbsoluteUrl(define.seo.og.image.logo) }]
    };

    /**
     * Title
     */
    if (page.title !== false) {
      head.title = page.title;
    }

    /**
     * Description
     */
    if (page.description !== false) {
      if (Array.isArray(head.meta)) {
        head.meta.push({ name: "description", content: page.description });
      }
    }

    /**
     * Keywords
     */
    if (page.keywords !== false) {
      if (Array.isArray(head.meta)) {
        if (Array.isArray(page.keywords)) {
          head.meta.push({ name: "keywords", content: page.keywords.join() });
        }
      }
    }

    /**
     * Author
     */
    if (page.author !== false) {
      if (Array.isArray(head.meta)) {
        head.meta.push({ name: "author", content: page.author });
      }
    }

    nuxt.runWithContext(() => useHead(head));

    var meta: UseSeoMetaInput = {
      applicationName: "Incigma",
      ogSiteName: "Incigma",
      ogType: "website",
      twitterCard: "summary_large_image"
    };

    if (og.description !== false) {
      meta.description = og.description;
    }

    if (og.author !== false) {
      meta.author = og.author;
    }

    if (og.ogUrl !== false) {
      meta.ogUrl = og.ogUrl;
    }

    if (og.ogTitle !== false) {
      meta.ogTitle = og.ogTitle;
    }

    if (og.ogDescription !== false) {
      meta.ogDescription = og.ogDescription;
    }

    if (og.ogImageUrl !== false) {
      meta.ogImageUrl = og.ogImageUrl;
    }

    if (og.ogImageAlt !== false) {
      meta.ogImageAlt = og.ogImageAlt;
    }

    /**
     * Twitter
     */
    if (og.twitterCard !== false) {
      meta.twitterCard = og.twitterCard;
    }

    if (og.twitterTitle !== false) {
      meta.twitterTitle = og.twitterTitle;
    }

    if (og.twitterDescription !== false) {
      meta.twitterDescription = og.twitterDescription;
    }

    if (og.twitterImageAlt !== false) {
      meta.twitterImageAlt = og.twitterImageAlt;
    }

    if (og.twitterImage !== false) {
      meta.twitterImage = { url: og.twitterImage };
    }

    nuxt.runWithContext(() => useSeoMeta(meta));
  };

  interface simple {
    title: string;
    description: string;
    keywords: string[];
    author: string | false;
    image: string;
    url?: string;
  }

  const simple = (simple: simple) => {
    simple.image = useAbsoluteUrl(simple.image)!;

    advanced(
      {
        title: simple.title,
        description: simple.description,
        keywords: simple.keywords,
        author: simple.author
      },
      {
        description: simple.description,
        author: simple.author,
        ogUrl: simple.url ?? useRequestURL().href,
        ogTitle: simple.title,
        ogDescription: simple.description,
        ogImageUrl: simple.image,
        ogImageAlt: simple.title,
        twitterTitle: simple.title,
        twitterDescription: simple.description,
        twitterImageAlt: simple.title,
        twitterImage: simple.image
      }
    );
  };

  return {
    provide: {
      seo: { simple, advanced }
    }
  };
});
